'use client'

import * as React from 'react'
import { useTranslations } from 'next-intl'
import * as Select from 'primitives/select'
import { CheckIcon, ChevronDownIcon } from 'primitives/icons'
import { tw } from 'utils/classnames'

import { ContentSearchInput } from '../algolia/components/search-input/deferred-input'
import type { CategoryPageType } from '../content-api/config'
import { mapCategoryPageTypeToCategoryDetails } from '../content-api/config'

export function Search() {
  const [category, setCategory] = React.useState<CategoryPageType>('png')
  const t = useTranslations('not_found')
  const pageDetails = mapCategoryPageTypeToCategoryDetails[category]
  const dropDownItems: { label: string; value: CategoryPageType }[] = [
    {
      label: t('categories.pngs'),
      value: 'png',
    },
    {
      label: t('categories.mockups'),
      value: 'mockups',
    },
    {
      label: t('categories.kawaii_images'),
      value: 'kawaii',
    },
    {
      label: t('categories.cartoons'),
      value: 'cartoons',
    },
    {
      label: t('categories.backgrounds'),
      value: 'backgrounds',
    },
    {
      label: t('categories.patterns'),
      value: 'patterns',
    },
    {
      label: t('categories.frames'),
      value: 'frames',
    },
    {
      label: t('categories.quotes'),
      value: 'quotes',
    },
    {
      label: t('categories.borders_or_corners'),
      value: 'borders-and-corners',
    },
    {
      label: t('categories.sketches'),
      value: 'sketches',
    },
    {
      label: t('categories.coloring_pages'),
      value: 'coloring-pages',
    },
    {
      label: t('categories.stockphotos'),
      value: 'stockphotos',
    },
  ]

  return (
    <div className="m-auto mb-16 flex w-full max-w-3xl flex-col gap-2 min-[575px]:flex-row">
      <Select.Root
        name="option"
        required
        onValueChange={value => {
          setCategory(value as CategoryPageType)
        }}
      >
        <Select.Trigger
          aria-label={'Select category'}
          className="placeholder-transition bg-cf-white text-14 flex h-full w-full items-center justify-between bg-white px-5 py-[10px] pr-10 text-[#261D35] shadow-[0px_5px_15px_0px_#0000001A] placeholder:text-[#9F9CAB] focus:outline-none min-[575px]:w-[300px]"
        >
          <Select.Value placeholder={t('categories.pngs')} />
          <Select.Icon>
            <ChevronDownIcon className="ml-2 size-4 text-gray-500" />
          </Select.Icon>
        </Select.Trigger>
        <Select.Content
          className="bg-cf-white shadow-cf-elevation-light-5 relative z-[1001] w-full p-[10px]"
          position="popper"
          align="center"
          alignOffset={50}
          sideOffset={4}
        >
          <Select.Viewport className="bg-cf-white flex w-[calc(var(--radix-popper-anchor-width)-2*10px)] flex-col gap-1 p-[2px]">
            {dropDownItems.map(({ value, label }) => (
              <Select.Item
                key={label}
                value={value}
                className={tw(
                  'rounded-2 flex h-[32px] cursor-pointer items-center justify-start px-3',
                  'hover:bg-cf-lavender text-14 flex w-[100%] rounded-full',
                )}
              >
                <Select.ItemText>{label}</Select.ItemText>
                <Select.ItemIndicator className={tw('ml-2 flex size-4 flex-shrink-0 items-center')}>
                  <CheckIcon />
                </Select.ItemIndicator>
              </Select.Item>
            ))}
          </Select.Viewport>
        </Select.Content>
      </Select.Root>
      <ContentSearchInput
        href={pageDetails.searchPage}
        enableAutocomplete={true}
        filters={`authorId:${pageDetails.authorId}`}
        placeholder={t('search.placeholder', {
          categoryName: pageDetails.contentCategory,
        })}
        no_results_text={t('search.no_results', {
          categoryName: pageDetails.contentCategory,
        })}
        searchSource="not-found-search"
      />
    </div>
  )
}
